import dayjs, { Dayjs } from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import {
  TiendaPerfectaDistributor,
  TiendaPerfectaVisitHistoryNamed,
} from 'models/TiendaPerfecta';

import {
  DateRange,
  MAX_RANGE_DAYS,
  clampRange,
  getDefaultRange,
  validateRange,
} from './range';

dayjs.extend(utc);
dayjs.extend(timezone);

const DATE_FORMAT = 'YYYY-MM-DD';

export interface VisitHistoryFilterState {
  range: DateRange;
  zoneIds: string[];
  distributorIds: string[];
}

const parseList = (raw: string | null): string[] =>
  (raw ?? '')
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean);

/**
 * Filtros desde la URL. Cualquier valor invalido cae por `clampRange` al rango
 * por defecto, y un fin futuro se recorta a hoy en el huso del usuario: no hay
 * ejecucion que reportar de un dia que no ocurrio.
 */
export const parseVisitHistoryQuery = (
  params: URLSearchParams,
  now: Dayjs,
  tz: string
): VisitHistoryFilterState => {
  const def = getDefaultRange(now, tz);
  const clamped = clampRange(
    { from: params.get('from') ?? def.from, to: params.get('to') ?? def.to },
    now,
    tz
  );

  const today = now.tz(tz).format(DATE_FORMAT);
  const to = clamped.to > today ? today : clamped.to;
  const from = clamped.from > to ? to : clamped.from;

  return {
    range: { from, to },
    zoneIds: parseList(params.get('zoneIds')),
    distributorIds: parseList(params.get('distributorIds')),
  };
};

export const serializeVisitHistoryQuery = (
  filters: VisitHistoryFilterState
): URLSearchParams => {
  const params = new URLSearchParams();
  params.set('from', filters.range.from);
  params.set('to', filters.range.to);
  if (filters.zoneIds.length) params.set('zoneIds', filters.zoneIds.join(','));
  if (filters.distributorIds.length) {
    params.set('distributorIds', filters.distributorIds.join(','));
  }

  return params;
};

/** Zonas unicas de los distribuidores alcanzables; `zone` es opcional. */
export const zoneOptions = (
  distributors: TiendaPerfectaDistributor[],
  unnamedLabel = ''
): TiendaPerfectaVisitHistoryNamed[] => {
  const zones = new Map<string, TiendaPerfectaVisitHistoryNamed>();
  distributors.forEach((d) => {
    if (d.zone) {
      zones.set(d.zone.id, {
        id: d.zone.id,
        name: d.zone.name?.trim() || unnamedLabel,
      });
    }
  });

  return [...zones.values()].sort((a, b) => a.name.localeCompare(b.name));
};

/** Sin zonas elegidas no acota; con zonas, solo los distribuidores de ellas. */
export const narrowDistributors = (
  distributors: TiendaPerfectaDistributor[],
  zoneIds: string[]
): TiendaPerfectaDistributor[] =>
  zoneIds.length
    ? distributors.filter((d) => d.zone && zoneIds.includes(d.zone.id))
    : distributors;

/**
 * Keeps only the distributor ids still offered by the chosen zones, so a stale
 * URL id (or one a zone change dropped) never reaches the API or the select.
 */
export const pruneDistributorIds = (
  distributors: TiendaPerfectaDistributor[],
  zoneIds: string[],
  ids: string[]
): string[] => {
  const offered = new Set(
    narrowDistributors(distributors, zoneIds).map((d) => d.id)
  );

  return ids.filter((id) => offered.has(id));
};

/** Keeps only the zone ids that the scoped distributors actually offer. */
export const pruneZoneIds = (
  distributors: TiendaPerfectaDistributor[],
  ids: string[]
): string[] => {
  const offered = new Set(zoneOptions(distributors).map((z) => z.id));

  return ids.filter((id) => offered.has(id));
};

/** True when `date` is a calendar day after today in the user's time zone. */
export const isAfterToday = (date: Dayjs, now: Dayjs, tz: string): boolean =>
  date.format(DATE_FORMAT) > now.tz(tz).format(DATE_FORMAT);

/**
 * True when the URL carries two valid dates spanning more than the 31-day
 * contract limit (an invalid or partial pair falls to the default range and
 * needs no warning).
 */
export const isRangeTooLong = (params: URLSearchParams): boolean => {
  const from = params.get('from');
  const to = params.get('to');
  if (!from || !to) return false;

  const check = validateRange({ from, to });

  return !check.valid && check.reason === 'too_long';
};

/**
 * Range picker rule: no future days and at most 31 days from the endpoint
 * already picked (`from` is that endpoint, when there is one).
 */
export const isRangeDayDisabled = (
  current: Dayjs,
  picked: Dayjs | null | undefined,
  now: Dayjs,
  tz: string
): boolean => {
  if (isAfterToday(current, now, tz)) return true;

  return Boolean(
    picked && Math.abs(current.diff(picked, 'day')) >= MAX_RANGE_DAYS
  );
};
